import { zodResolver } from '@hookform/resolvers/zod';
import { O } from '@mobily/ts-belt';
import { Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { usePropertyContext } from '../../../properties/components/SingleProperty';
import { useDeleteRack } from '../../hooks/useDelete';
import { usePatchRack } from '../../hooks/usePatch';
import { RackDetails } from '../../types/ObjectDetails';
import { rackSchema, RackSchema } from '../../types/RackSchema';
import { PhotosListWithUpload } from '../PhotosListWithUpload';

type Props = {
  details: RackDetails;
  onSave: Lambda<void, void>;
  onDelete: Lambda<void, void>;
};

export const Rack: FC<Props> = ({ details, onSave, onDelete }) => {
  const { t } = useTranslation();
  const { id: propertyId } = usePropertyContext();
  const { location, text, id } = details;
  const { mutate: patchRack, isLoading: isLoadingRack } = usePatchRack({
    onSuccess: () => {
      onSave();
      toast.success(t('general.updatedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });
  const { mutate: deleteRack, isLoading: isLoadingDeleteRack } = useDeleteRack({
    onSuccess: () => {
      onDelete();
      toast.success(t('general.deletedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<RackSchema>({
    defaultValues: {
      text: O.getWithDefault(text, ''),
      location: O.getWithDefault(location, ''),
    },
    mode: 'onChange',
    resolver: zodResolver(rackSchema),
  });

  const [photosURLs, setPhotosURLs] = useState(details.photosURLs ?? []);

  const handleSave = (values: RackSchema) => {
    patchRack({ ...values, id, photosURLs });
  };

  const handleDelete = () => {
    deleteRack(details.id);
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(handleSave)}>
      <Typography sx={{ fontSize: 18, paddingBottom: 2 }}>{t('areas.rack.label')}</Typography>
      <Stack gap={2} width="100%">
        <PhotosListWithUpload
          photosURLs={photosURLs}
          setPhotosUrls={setPhotosURLs}
          entityType="rack"
          entityId={id}
          propertyId={O.getWithDefault(propertyId, 0)}
        />
        <TextField label={t('areas.fields.name')} {...register('text')} disabled />
        <TextField label={t('areas.fields.location')} {...register('location')} />
      </Stack>
      <Grid container justifyContent="space-between" mt={2}>
        <Button variant="text" color="error" onClick={handleDelete} disabled={isLoadingDeleteRack}>
          {isLoadingDeleteRack ? <CircularProgress size={16} /> : t('general.delete')}
        </Button>
        <Button variant="contained" color="primary" type="submit" disabled={!isValid || isLoadingRack}>
          {isLoadingRack ? <CircularProgress size={16} /> : t('general.save')}
        </Button>
      </Grid>
    </Stack>
  );
};
